/**
 * Age range definitions used for specificity calculation.
 */
const AGE_RANGES = {
  '1-3': [1, 3],
  '4-6': [4, 6],
  '7-11': [7, 11],
  '12-16': [12, 16],
};
const MAX_AGE_SPAN = 16; // 0–16 is the full possible range

/**
 * Calculate a specificity score (0–1) for a single filter dimension.
 * Returns null if the filter is not applicable to this activity.
 */
function calcAgeSpecificity(activity, ageFilterValues) {
  if (!ageFilterValues || ageFilterValues.length === 0) return null;

  let bestScore = 0;
  for (const range of ageFilterValues) {
    const [lo, hi] = AGE_RANGES[range] || [0, 16];
    const filterSpan = hi - lo;
    const actMin = activity.age_min ?? 0;
    const actMax = activity.age_max ?? 16;
    const actSpan = actMax - actMin;
    // How much does the activity's range "overshoot" the filter range?
    const overshoot = Math.max(0, lo - actMin) + Math.max(0, actMax - hi);
    const maxOvershoot = Math.max(actSpan, filterSpan, MAX_AGE_SPAN);
    const s = 1 - overshoot / maxOvershoot;
    if (s > bestScore) bestScore = s;
  }
  return bestScore;
}

/**
 * Compute the combined specificity + popularity score for sorting.
 * When filters are active, season boost overrules popularity ranking.
 * @param {object} activity
 * @param {object} activeFilters - the filter state object from the page
 * @param {number} maxPopularity - max popularity_score among the filtered list (for normalisation)
 * @param {object} seasonSlugToId - slug→id map for seasons
 * @param {object} categorySlugToId - slug→id map for categories
 * @param {Array} seasons - all Season records with { id, months: number[] }
 * @returns {number} final score 0–1
 */
export function computeFilterScore(activity, activeFilters, maxPopularity, seasonSlugToId = {}, categorySlugToId = {}, seasons = []) {
  const hasFilters = Object.values(activeFilters).some(v => Array.isArray(v) && v.length > 0);
  if (!hasFilters) {
    // No filters: sort purely by popularity
    return (activity.popularity_score || 0) / (maxPopularity || 1);
  }

  const specificityScores = [];

  Object.entries(activeFilters).forEach(([categorySlug, selectedValues]) => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return;

    if (categorySlug === 'alter' || categorySlug === 'ages') {
      const s = calcAgeSpecificity(activity, selectedValues);
      if (s !== null) specificityScores.push(s);
      return;
    }

    if (categorySlug === 'kategorie' || categorySlug === 'category') {
      const selectedIds = selectedValues.map(slug => categorySlugToId[slug] || slug);
      const matchCount = selectedIds.filter(id => (activity.category_ids || []).includes(id)).length;
      specificityScores.push(matchCount / selectedValues.length);
      return;
    }

    if (categorySlug === 'jahreszeit' || categorySlug === 'seasons') {
      const selectedIds = selectedValues.map(slug => seasonSlugToId[slug]).filter(Boolean);
      if (selectedIds.length > 0) {
        const matchCount = selectedIds.filter(id => (activity.season_ids || []).includes(id)).length;
        specificityScores.push(matchCount / selectedIds.length);
      }
      return;
    }

    if (categorySlug === 'dauer' || categorySlug === 'duration') {
      specificityScores.push(selectedValues.includes(activity.duration) ? 1 : 0);
      return;
    }

    if (categorySlug === 'weitere_filter') {
      const matchCount = selectedValues.filter(v => (activity.weitere_filter || []).includes(v)).length;
      specificityScores.push(matchCount / selectedValues.length);
      return;
    }

    // Generic: if the activity passes, score 1
    specificityScores.push(1);
  });

  const avgSpecificity = specificityScores.length > 0
    ? specificityScores.reduce((a, b) => a + b, 0) / specificityScores.length
    : 0;

  const normalizedPopularity = (activity.popularity_score || 0) / (maxPopularity || 1);

  // When filters are active, season boost overrules popularity ranking
  const seasonBoost = getSeasonBoost(activity, seasons);
  return (avgSpecificity * 0.7 + normalizedPopularity * 0.3) * seasonBoost;
}

export function filterActivities(activities, filters = {}) {
  return activities.filter(activity => {
    // Region filter
    if (filters.region && activity.region_id !== filters.region) {
      return false;
    }

    // Category filter (category_ids on activity)
    if (filters.category && filters.category.length > 0) {
      if (!activity.category_ids || !filters.category.some(cat => activity.category_ids.includes(cat))) {
        return false;
      }
    }

    // Season filter (season_ids on activity)
    if (filters.season && filters.season.length > 0) {
      if (!activity.season_ids || !filters.season.some(s => activity.season_ids.includes(s))) {
        return false;
      }
    }

    // Duration filter
    if (filters.duration && filters.duration.length > 0) {
      if (!filters.duration.includes(activity.duration)) {
        return false;
      }
    }

    // Age filter
    if (filters.age && filters.age.length > 0) {
      const ageRanges = {
        '0-3': [0, 3],
        '4-6': [4, 6],
        '7-11': [7, 11],
        '12-16': [12, 16]
      };
      
      const ageMatch = filters.age.some(ageRange => {
        const [min, max] = ageRanges[ageRange] || [0, 99];
        return activity.age_min <= max && activity.age_max >= min;
      });
      
      if (!ageMatch) return false;
    }

    // Weitere Filter (indoor, outdoor, gratis, nachhaltig, etc.)
    if (filters.weitere_filter && filters.weitere_filter.length > 0) {
      if (!activity.weitere_filter || !filters.weitere_filter.some(f => activity.weitere_filter.includes(f))) {
        return false;
      }
    }

    // Opening Days filter - check if activity is open on selected days
    if (filters.opening_days && filters.opening_days.length > 0) {
      const dayMapping = {
        'montag': 'monday',
        'dienstag': 'tuesday',
        'mittwoch': 'wednesday',
        'donnerstag': 'thursday',
        'freitag': 'friday',
        'samstag': 'saturday',
        'sonntag': 'sunday',
        'monday': 'monday',
        'tuesday': 'tuesday',
        'wednesday': 'wednesday',
        'thursday': 'thursday',
        'friday': 'friday',
        'saturday': 'saturday',
        'sunday': 'sunday'
      };

      const hasMatchingDay = filters.opening_days.some(day => {
        const englishDay = dayMapping[day.toLowerCase()];
        return activity.opening_hours?.[englishDay] && activity.opening_hours[englishDay].trim() !== '';
      });

      if (!hasMatchingDay) return false;
    }

    return true;
  });
}

export function getTimeSlot() {
  const hour = new Date().getHours();
  if (hour < 11) return 'morning';
  if (hour < 15) return 'midday';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

// Returns the hour range [start, end) for a given slot
export function getTimeSlotHours(slot) {
  const ranges = { morning: [6, 11], midday: [11, 15], afternoon: [15, 18], evening: [18, 23] };
  return ranges[slot] || [6, 23];
}

export function getTimeSlotLabel(slot, language = 'de') {
  const labels = {
    de: {
      morning: 'Heute Morgen',
      midday: 'Heute Mittag',
      afternoon: 'Heute Nachmittag',
      evening: 'Heute Abend'
    },
    en: {
      morning: 'This Morning',
      midday: 'Today at Noon',
      afternoon: 'This Afternoon',
      evening: 'This Evening'
    }
  };
  return labels[language]?.[slot] || labels.de[slot];
}

/**
 * Check if the current month matches any of the provided seasons.
 * If the activity has no season_ids, it's considered valid for all seasons.
 * @param {object} activity
 * @param {Array} seasons - all Season records with { id, months: number[] }
 * @returns {boolean}
 */
export function isInCurrentSeason(activity, seasons) {
  if (!activity.season_ids || activity.season_ids.length === 0) return true;
  const currentMonth = new Date().getMonth() + 1; // 1–12
  return activity.season_ids.some(seasonId => {
    const season = seasons.find(s => s.id === seasonId);
    return season && (season.months || []).includes(currentMonth);
  });
}

/**
 * Returns a season specificity multiplier for sorting.
 * Activities explicitly tagged for the current season rank higher than year-round ones.
 * 1.5 = explicitly seasonal match, 1.0 = year-round (no season_ids), 0.5 = season mismatch (shouldn't appear but safe fallback)
 */
export function getSeasonBoost(activity, seasons) {
  if (!activity.season_ids || activity.season_ids.length === 0) return 1.0; // year-round
  const currentMonth = new Date().getMonth() + 1;
  const matches = activity.season_ids.some(seasonId => {
    const season = seasons.find(s => s.id === seasonId);
    return season && (season.months || []).includes(currentMonth);
  });
  return matches ? 1.5 : 0.5;
}

// Check if an activity is open during a given time slot
export function isOpenDuringSlot(activity, slot) {
  if (activity.always_open) {
    const [slotStart, slotEnd] = getTimeSlotHours(slot);
    // always_open treated as 06:00–22:00
    return slotStart < 22 && slotEnd > 6;
  }
  const now = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[now.getDay()];
  const hours = activity.opening_hours?.[currentDay];
  if (!hours || hours.toLowerCase() === 'geschlossen' || hours.toLowerCase() === 'closed') return false;
  const match = hours.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
  if (!match) return false;
  const openMin = parseInt(match[1]) * 60 + parseInt(match[2]);
  const closeMin = parseInt(match[3]) * 60 + parseInt(match[4]);
  const [slotStart, slotEnd] = getTimeSlotHours(slot);
  // Activity must overlap with the slot
  return openMin < slotEnd * 60 && closeMin > slotStart * 60;
}